import { StargraveRewireStat, StargraveSoldier, StargraveSoldierProfile, StargraveStat, StargraveStatLine } from '../types';
import { getAllStargraveSoldiers } from '../stores/stargraveCustomStore';
import { StatModifier } from '../utils/splitStats';

export interface StargraveTrait {
  id: string;
  name: string;
  description?: string | null;
}

export const STARGRAVE_ROBOT_TRAIT: StargraveTrait = {
  id: 'robot',
  name: 'Robot',
  description: 'Never wounded; immune to toxins and gases; cannot be treated by a medic kit.',
};

export const formatStargraveSoldierCategory = (category: StargraveSoldierProfile['category']): string =>
  category === 'specialist' ? 'Specialist' : category === 'creature' ? 'Creature' : 'Standard';

export function getStargraveSoldierTraits(isRobot: boolean, profile?: StargraveSoldierProfile, rewiredStat?: StargraveRewireStat): StargraveTrait[] {
  return [
    ...(isRobot ? [STARGRAVE_ROBOT_TRAIT] : []),
    ...(rewiredStat ? [{
      id: 'rewired',
      name: `Re-wired: +1 ${rewiredStat[0].toUpperCase()}${rewiredStat.slice(1)}`,
      description: `+1 ${rewiredStat[0].toUpperCase()}${rewiredStat.slice(1)}, -1 Will. Permanent; a robot may only be re-wired once.`,
    }] : []),
    ...(profile?.notes ? [{ id: `${profile.id}-special`, name: profile.notes.split(/[;.]/)[0].trim(), description: profile.notes }] : []),
  ];
}

/** Re-wire Robot: permanent +1 to the chosen stat, -1 Will. */
export function applyStargraveRewireModifiers(stats: StargraveStatLine, rewiredStat?: StargraveRewireStat): StargraveStatLine {
  if (!rewiredStat) return stats;
  return { ...stats, [rewiredStat]: stats[rewiredStat] + 1, will: stats.will - 1 };
}

export function getStargraveRewireStatModifiers(rewiredStat: StargraveRewireStat | undefined, stat: StargraveStat): StatModifier[] {
  if (!rewiredStat) return [];
  if (stat === rewiredStat) return [{ label: 'Re-wired', value: 1, type: 'add' }];
  if (stat === 'will') return [{ label: 'Re-wired', value: -1, type: 'add' }];
  return [];
}

export interface RecruitStargraveSoldierInput {
  profileId: string;
  name?: string;
  notes?: string;
  isRobot: boolean;
  availableCredits: number;
}

export function buildStargraveSoldier(input: RecruitStargraveSoldierInput): StargraveSoldier {
  const profile = getAllStargraveSoldiers().find((candidate) => candidate.id === input.profileId);
  if (!profile) throw new Error('Select a valid soldier profile.');
  if (input.availableCredits < profile.cost) throw new Error('Not enough credits to recruit this soldier.');

  return {
    id: crypto.randomUUID(),
    name: input.name?.trim() || profile.name,
      notes: input.notes?.trim() || undefined,
    profileId: profile.id,
    isRobot: input.isRobot,
    gameState: {
      currentHealth: profile.health,
      stunned: false,
      poisoned: false,
      loot: null,
      leftTable: false,
      activeBuffs: [],
    },
  };
}